import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Text, mergeClasses } from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import type { OperationDefinition } from '@/types/operations';
import { HighlightText } from './HighlightText';
import { OperationFavoriteButton } from './OperationFavoriteButton';
import { OperationInfoButton } from './OperationInfoButton';
import { useConnectorSectionStyles } from './ConnectorSection.styles';

type ConnectorOperationsListProps = {
  operations: OperationDefinition[];
  onOperationSelect: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  /** Left-rail color for the operations list (typically the connector brand color). */
  brandColor?: string | undefined;
  /** When set and operations exceed it, a "see all" button is rendered. */
  initialLimit?: number | undefined;
  showAll?: boolean;
  onShowAllChange?: (showAll: boolean) => void;
  /** When set, the connector section's outer expand state — used to reset internal search on collapse. */
  isExpanded?: boolean;
  /**
   * Notifies the parent when the displayed operation count changes (e.g. due
   * to internal search). The picker uses this to recompute virtualized row
   * heights.
   */
  onDisplayedCountChange?: (count: number, isSearching: boolean) => void;
  /**
   * Outer search query (from the picker's main search bar), used to highlight
   * matching text in operation names without filtering. Separate from the
   * per-section search query the user types into the input here.
   */
  outerSearchQuery?: string;
  /** Highlight matching text in operation names using the per-section query. */
  highlightInternalQuery?: boolean;
};

/**
 * Operation list with optional per-section search, see-all behavior, and
 * brand-color accent rail. Shared by `ConnectorSection` and
 * `AzureConnectorSection` to remove a sizable block of duplication.
 */
export const ConnectorOperationsList = ({
  operations,
  onOperationSelect,
  brandColor,
  initialLimit,
  showAll = false,
  onShowAllChange,
  isExpanded = true,
  onDisplayedCountChange,
  outerSearchQuery = '',
  highlightInternalQuery = false,
}: ConnectorOperationsListProps) => {
  const styles = useConnectorSectionStyles();
  const [internalQuery, setInternalQuery] = useState('');

  const showSearch = !!initialLimit && operations.length > initialLimit;

  const filteredOperations = useMemo(() => {
    if (!internalQuery.trim()) return operations;
    const q = internalQuery.toLowerCase();
    return operations.filter(
      (op) =>
        op.name.toLowerCase().includes(q) ||
        op.description?.toLowerCase().includes(q),
    );
  }, [operations, internalQuery]);

  const displayedOperations = useMemo(() => {
    if (!initialLimit || showAll || filteredOperations.length <= initialLimit) {
      return filteredOperations;
    }
    return filteredOperations.slice(0, initialLimit);
  }, [filteredOperations, initialLimit, showAll]);

  const hasMore = !!initialLimit && filteredOperations.length > initialLimit && !showAll;

  useEffect(() => {
    if (!isExpanded) setInternalQuery('');
  }, [isExpanded]);

  useEffect(() => {
    if (!onDisplayedCountChange) return;
    onDisplayedCountChange(filteredOperations.length, internalQuery.trim().length > 0);
  }, [filteredOperations.length, internalQuery, onDisplayedCountChange]);

  const highlightQuery = highlightInternalQuery
    ? outerSearchQuery || internalQuery
    : outerSearchQuery;

  return (
    <>
      {showSearch && (
        <div className={styles.searchContainer}>
          <Input
            className={styles.searchInput}
            placeholder="Search operations in this connector..."
            contentBefore={<SearchRegular />}
            contentAfter={
              internalQuery ? (
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={() => setInternalQuery('')}
                  aria-label="Clear search"
                />
              ) : null
            }
            value={internalQuery}
            onChange={(_, data) => setInternalQuery(data.value)}
            size="small"
            aria-label="Search operations in this connector"
          />
        </div>
      )}
      {internalQuery && filteredOperations.length === 0 ? (
        <div className={styles.emptyState}>
          <Text>No operations match &ldquo;{internalQuery}&rdquo;</Text>
        </div>
      ) : (
        <ul
          className={styles.operationsList}
          role="list"
          style={{ borderLeftColor: brandColor }}
        >
          {displayedOperations.map((operation, index) => (
            <li
              key={operation.id}
              className={mergeClasses(
                styles.operationListItem,
                styles.operationEnter,
              )}
              style={{ ['--stagger-index' as string]: index } as React.CSSProperties}
            >
              {/*
                Rendered as `<div role="button">` (not a native `<button>`) so
                the inner info/favorite controls — which are focusable
                role="button" spans — don't create an invalid
                `<button>`-inside-`<button>` HTML tree. Keyboard activation
                (Enter / Space) is wired manually; the `target === currentTarget`
                guard prevents key events bubbling up from the nested controls
                from double-triggering the row.
              */}
              <div
                role="button"
                tabIndex={0}
                className={styles.operationItem}
                onClick={(e) => onOperationSelect(operation, e)}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOperationSelect(operation);
                  }
                }}
                aria-label={`Add ${operation.name}`}
              >
                {highlightInternalQuery || outerSearchQuery ? (
                  <HighlightText
                    text={operation.name}
                    query={highlightQuery}
                    className={styles.operationName}
                  />
                ) : (
                  <Text className={styles.operationName}>{operation.name}</Text>
                )}
                <OperationInfoButton operation={operation} />
                <OperationFavoriteButton operation={operation} />
              </div>
            </li>
          ))}
          {hasMore && (
            <li className={styles.operationListItem}>
              <button
                type="button"
                className={styles.seeAllButton}
                onClick={() => onShowAllChange?.(true)}
              >
                <Text>See all {filteredOperations.length} operations</Text>
              </button>
            </li>
          )}
        </ul>
      )}
    </>
  );
};

export default ConnectorOperationsList;
